/**
 * 허브 전환 띠의 **문구와 가는 곳** — 면마다 맞는 Research 갈래로 보낸다.
 *
 * ```
 *   소셜포스팅  /service/research/social?q=   {계정}이 올렸거나 언급된 · {브랜드}가 언급되거나 그 이름의 계정이 올린
 *                                                 모든 소셜 포스팅을 다 확인하고 싶다면?
 *   광고        /service/research/ads?q=      {대상} 이름으로 메타 광고를 조건별로 한번에 찾아보고 싶다면?
 *   상품        /service/research/products?q= 국내 {n}개 몰, 아마존까지 {대상} 상품 상세를 한번에 보고 싶다면?
 *   캠페인      /service/research/events?q=   {대상}이 언제 어떤 프로모션을 했는지 캘린더로 한번에 보고 싶다면?
 * ```
 *
 * ⛔ **허브의 건수를 적지 마라 · 「나머지」를 약속하지 마라** — 허브와 Research 는 다른 글을 고른다.
 *   수는 플랜(`searchDays`·`searchRows`)과 상수(국내 몰 수)에서만 읽는다.
 * ⚠️ **칸은 그 갈래에 실제로 있는 것만** 적는다 — 광고·상품·이벤트 갈래에는 Excel 내보내기가 없다.
 * ⚠️ 소셜 갈래의 `?q=` 는 **본문과 계정 아이디를 함께** 찾는다(고정 검색 조건) — 그래서 「언급된」만이 아니라 「올린」 글도 나온다.
 * ⚠️ 광고 갈래의 `?q=` 는 광고 문구·제목·페이지 이름을 **글자로** 찾는다 — 그래서 「{대상} 이름으로」다.
 * 🔑 이번 달 Research 횟수가 정해진 플랜(LITE)이면 단추 옆에 「Research 1회 사용」을 단다 — 가면 1회로 센다.
 *
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 변환이다.
 */

import type { AnalysisTab } from 'src/sections/search-all-analysis/constants/search-all-analysis';

import { paths } from 'src/routes/paths';

import { fNumber } from 'src/utils/format/format-number';

import { RESEARCH_ONE_USE_HINT } from 'src/sections/common/research-quota/research-quota-copy';

// ----------------------------------------------------------------------

/** 차이 하나 — 이름과 그 값 */
export type HubConversionPoint = {
  /** 무엇이 다른가 — 「더 과거까지」 */
  label: string;
  /** 얼마나 다른가 — 「최근 365일」 */
  value: string;
};

/** 띠 한 벌 — 머리 · 칸 · 가는 곳 · (한도 있는 플랜만) 단추 옆 귀띔 */
export type HubResearchCopy = {
  title: string;
  points: HubConversionPoint[];
  href: string;
  /** 「Research 1회 사용」 — 이번 달 횟수가 정해진 플랜(LITE)만. 무제한이면 없다 */
  hint?: string;
};

/** 허브가 보고 있는 대상 */
export type HubResearchSubject = 'brand' | 'account';

/** 문구가 적는 수와 들고 가는 말 — 전부 정본에서 읽어 온다 */
export type HubResearchFacts = {
  subject: HubResearchSubject;
  /** 들고 가는 말 — 브랜드 정규명 · 계정 아이디. 비었으면 `?q=` 를 안 싣는다 */
  name: string;
  /**
   * 그 고객의 플랜 한도(`usePlanSearchLimits`) — 기간·건수는 소셜 갈래만 읽는다.
   * `researchMonthly` 가 0 보다 큰 수면(LITE) 모든 갈래에 「Research 1회 사용」을 단다.
   */
  limits: { searchDays: number; searchRows: number; researchMonthly?: number | null };
  /** 국내 판매몰 수(`DOMESTIC_SOURCE_COUNT`) — 상품 갈래만 읽는다. 0 이면 몰 수를 안 적는다 */
  domesticMalls: number;
};

/**
 * 소셜 갈래의 칸 — 플랜이 정한 수를 이름과 함께 편다.
 * @param limits 그 고객의 플랜 한도 — 0 이면 그 칸을 뺀다
 * @returns 넷까지 — 기간 · 한 번에 받는 건수 · 조건 · 내보내기
 */
export function toHubConversionPoints(limits: { searchDays: number; searchRows: number }): HubConversionPoint[] {
  return [
    ...(limits.searchDays > 0
      ? [{ label: '더 과거까지', value: `최근 ${fNumber(limits.searchDays)}일` }]
      : []),
    ...(limits.searchRows > 0
      ? [{ label: '한 번에', value: `최대 ${fNumber(limits.searchRows)}건` }]
      : []),
    { label: '조건을 자유롭게', value: '채널·국가·형식·기간' },
    { label: '내보내기', value: 'Excel' },
  ];
}

/**
 * 면에 맞는 Research 갈래의 띠 한 벌.
 * @param tab 가는 갈래 — 허브의 면과 같은 것을 다루는 갈래
 * @param facts 대상 · 들고 가는 말 · 플랜과 상수에서 읽은 수
 * @returns 머리(묻는 꼴) · 칸(넷까지) · `/service/research/<갈래>?q=<말>`
 */
export function toHubResearchCopy(tab: AnalysisTab, facts: HubResearchFacts): HubResearchCopy {
  const copy = toHubResearchBand(tab, facts);
  const monthly = facts.limits.researchMonthly;
  return typeof monthly === 'number' && monthly > 0 ? { ...copy, hint: RESEARCH_ONE_USE_HINT } : copy;
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 갈래마다의 머리 · 칸 · 가는 곳 */
function toHubResearchBand(tab: AnalysisTab, facts: HubResearchFacts): HubResearchCopy {
  const who = facts.subject === 'brand' ? '이 브랜드' : '이 계정';
  const word = facts.name.trim();
  const href = `${paths.service.searchAllAnalysisTab(tab)}${word ? `?q=${encodeURIComponent(word)}` : ''}`;

  if (tab === 'ads') {
    return {
      title: `${who} 이름으로 메타 광고를 조건별로 한번에 찾아보고 싶다면?`,
      points: AD_POINTS,
      href,
    };
  }

  if (tab === 'products') {
    const malls = facts.domesticMalls > 0 ? `국내 ${fNumber(facts.domesticMalls)}개 몰, 아마존까지 ` : '';
    return {
      title: `${malls}${who} 상품 상세를 한번에 보고 싶다면?`,
      points: PRODUCT_POINTS,
      href,
    };
  }

  if (tab === 'events') {
    return {
      title: `${who}${facts.subject === 'brand' ? '가' : '이'} 언제 어떤 프로모션을 했는지 캘린더로 한번에 보고 싶다면?`,
      points: EVENT_POINTS,
      href,
    };
  }

  const socialWho = facts.subject === 'brand'
    ? `${who}가 언급되거나 그 이름의 계정이 올린`
    : `${who}이 올렸거나 언급된`;
  return {
    title: `${socialWho} 모든 소셜 포스팅을 다 확인하고 싶다면?`,
    points: toHubConversionPoints(facts.limits),
    href,
  };
}

/** 광고 갈래 — 사이드바 좁히기 칸 · 정렬 · 담아서 비교. 플랜 한도가 없는 갈래라 수가 없다 */
const AD_POINTS: HubConversionPoint[] = [
  { label: '조건을 자유롭게', value: '집행 상태·국가·미디어 유형·랜딩' },
  { label: '정렬', value: '게재기간·최신 게재' },
  { label: '골라 담아', value: '카피·CTA·랜딩 비교' },
];

/** 상품 갈래 — 좁히기 칸 · 정렬 · 상세 · 담아서 비교 */
const PRODUCT_POINTS: HubConversionPoint[] = [
  { label: '조건을 자유롭게', value: '판매몰·가격대·상세페이지 태그' },
  { label: '정렬', value: '가격·할인율·리뷰 수' },
  { label: '상세까지', value: '순위·가격 변동·리뷰' },
  { label: '골라 담아', value: '나란히 비교' },
];

/** 이벤트 갈래 — 좁히기 칸 · 보기 · 근거 */
const EVENT_POINTS: HubConversionPoint[] = [
  { label: '조건을 자유롭게', value: '국가·유형·기간' },
  { label: '보기', value: '카드·캘린더' },
  { label: '근거까지', value: '게시물·광고·상품' },
];
